export interface GraphData {
  '@odata.context': string;
  businessPhones: string[];
  displayName: string;
  givenName: string;
  jobTitle: string;
  mail: string;
  mobilePhone: string | null;
  officeLocation: string;
  preferredLanguage: string;
  surname: string;
  userPrincipalName: string;
  id: string;
}

export enum UserRole {
  CHECKPOINT_MANAGER = 'CheckpointManager',
  DR_VIEWER = 'DRView',
  NETWORK_VIEWER = 'NetworkView',
  NOVA_VIEWER = 'NOVAView',
  RELEASE_NOTES_MANAGER = 'ReleaseNotesManager',
  USER_ACCESS_MANAGER = 'UserAccessManager',
  USER_ACCESS_MANAGER_NOVA = 'UserAccessManagerNova',
  JIRA_BOARD_MANAGER = 'JiraBoardManager',
  ORR_BACKGROUND_JOBS_MANAGER = 'OrrBackgroundJobsManager',
}

export interface UserRoleAccess {
  accessibleEndpoints: {
    methods: string[];
    path: string;
    supportedApiVersions: string[];
    versioningURLs: string[];
  }[];
  user: {
    name: string;
    email: string;
    roles: UserRole[];
  };
}

// --- User access management (Entra app-role assignment) ---
// Mirrors the backend DTOs in src/Models/V4/UserAccess.cs.

/** An assignable Entra app role exposed by the API (catalog entry). */
export interface AppRole {
  id: string;
  value: string;
  displayName: string;
  description: string;
}

/** A single app-role assignment held by a principal. */
export interface RoleAssignment {
  assignmentId: string;
  appRoleId: string;
  roleValue: string;
  roleDisplayName: string;
}

/** A principal (user/group/SP) that currently holds one or more roles. */
export interface RoleHolder {
  principalId: string;
  principalDisplayName: string;
  principalType: 'User' | 'Group' | 'ServicePrincipal';
  userPrincipalName: string | null;
  mail: string | null;
  assignments: RoleAssignment[];
}

/** A directory user returned by the Entra typeahead search. */
export interface EntraUser {
  id: string;
  displayName: string;
  mail: string | null;
  userPrincipalName: string;
  isGuest: boolean;
}

export interface AssignRoleRequest {
  principalId: string;
  roleValue: string;
  principalType?: string;
}

// --- Application access management (Cosmos Applications allowlist + Entra consent) ---
// Mirrors the backend DTOs in src/Models/V4/ApplicationAccess.cs.

/** An Entra app (enterprise app or app registration) from the directory search. */
export interface AppSearchResult {
  appId: string;
  objectId: string | null;
  displayName: string;
  source: 'ServicePrincipal' | 'AppRegistration';
  isManaged: boolean;
}

/** A Conformance role for a managed app: the Pegasus (Cosmos) allow flag + read-only Entra consent. */
export interface AppRolePermission {
  roleValue: string;
  roleDisplayName: string;
  pegasusAllowed: boolean;
  entraGranted: boolean;
  adminConsentStatus: string; // "Granted" | "Admin consent not granted"
}

/** A managed application joined with its Entra admin-consent state. */
export interface ManagedApp {
  appId: string;
  objectId: string | null;
  displayName: string;
  entraResolved: boolean;
  roles: AppRolePermission[];
}

export interface AddManagedAppRequest {
  appId: string;
  name: string;
  roles: string[];
}

export interface UpdateManagedAppRolesRequest {
  roles: string[];
}

// --- Role management (Entra app-role definitions) ---
// Mirrors CreateRoleRequest / UpdateRoleRequest in src/Models/V4/UserAccess.cs.

export interface CreateRoleRequest {
  value: string;
  displayName?: string;
  description?: string;
}

export interface UpdateRoleRequest {
  displayName?: string;
  description?: string;
  isEnabled: boolean;
}

// --- View management (SystemConfiguration.views) ---
// Mirrors ViewSettings / ViewUpsertRequest in src/Models/V4/SystemConfiguration.cs.

/** An evaluation view and the role required to access it (null = everyone, "*" = any app-only caller). */
export interface ViewSetting {
  name: string;
  requiredAccessRole: string | null;
}

export interface ViewUpsertRequest {
  requiredAccessRole: string | null;
}

/** Accountable-team (display value) -> Jira project key, for Report-a-Bug. */
export type AccountableJiraBoards = Record<string, string>;

/** An application allowlist entry as returned by the unified catalog. */
export interface ApplicationAccessSetting {
  id: string;
  name: string | null;
  roles: string[];
}

/** Unified catalog for the Roles & Views Manager UI (GET /system/catalog). */
export interface SystemCatalog {
  roles: AppRole[];
  views: ViewSetting[];
  applications: ApplicationAccessSetting[];
  /** Accountable-team (display value) -> Jira project key, for Report-a-Bug. */
  accountableJiraBoards: AccountableJiraBoards;
}
